/**
 * Client-side helpers for the weekday booking discount.
 *
 * The discount is configured per listing (`publicData.weekdayBookingDiscountPercent`)
 * and the authoritative amount is always computed on the server
 * (see server/api-util/lineItems.js): it only applies to bookings whose start
 * day falls Monday–Friday in the listing timezone. These helpers drive UI only —
 * e.g. the "on offer" price + badge shown on the listing card when the current
 * search targets a weekday date. Keep the allowed percentages in sync with the
 * server copy.
 */
import { parse } from './urlHelpers';

// Same set honoured by the server. Anything else is treated as "no discount".
export const WEEKDAY_DISCOUNT_PERCENTS = [10, 20, 30, 40, 50];

/** Resolve the listing's weekday discount percentage (10/20/30) or 0 when unset/invalid. */
export const getWeekdayBookingDiscountPercent = publicData => {
  const raw = Number(publicData?.weekdayBookingDiscountPercent);
  return WEEKDAY_DISCOUNT_PERCENTS.includes(raw) ? raw : 0;
};

/** ISO weekday (1=Mon … 7=Sun) for a `YYYY-MM-DD` calendar date, or null if unparseable. */
const ymdIsoWeekday = ymd => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
  if (!m) {
    return null;
  }
  // Build at UTC midnight so the weekday is the calendar date's own day, free of
  // the runtime timezone (the search `dates` param is a plain calendar date).
  const dt = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  const dow = dt.getUTCDay(); // 0=Sun … 6=Sat
  return dow === 0 ? 7 : dow;
};

/** Whether a `YYYY-MM-DD` date is Monday–Friday. */
export const isWeekdayYmd = ymd => {
  const iso = ymdIsoWeekday(ymd);
  return iso != null && iso >= 1 && iso <= 5;
};

/** Start date (`YYYY-MM-DD`) from a search string's `dates=start,end` param, or null. */
export const getSearchStartYmd = search => {
  if (!search) {
    return null;
  }
  const parsed = parse(search);
  const raw = parsed?.dates;
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const start = String(raw)
    .split(',')[0]
    ?.trim();
  return start || null;
};

/** Whether the current search targets a weekday (Mon–Fri) start date. */
export const isWeekdaySearch = search => isWeekdayYmd(getSearchStartYmd(search));
